import { TBundleLineInTransactionLogPage, TBundleLineInTransactionLogRow } from '@monorepo/types';
import { TTableOptions, TTableTreeModel } from '@monorepo/ui';
import moment from 'moment';
import { ModifiersZTableModel } from '../../../table.modifier';
import { TActionCellType, TTableCellTree, TTextCell } from '../../../table.type';

const formatDateTime = (time: number): string => moment(time).format('DD MMMM') + ', ' + moment(time).format('hh:mm A');

// Column keys come from the view descriptor config, which still uses the store data log keys.
const getValue = (
  header: TBundleLineInTransactionLogPage['table_config']['table_main_headers'][number],
  item: TBundleLineInTransactionLogRow,
): string | number => {
  switch (header.key) {
    case 'datetime':
      return formatDateTime(item.last_at);
    case 'actual_qty':
      return item.quantity_scanned;
    case 'tagQty':
      return item.tag_quantity;
    case 'remaining':
      return item.remaining;
    case 'tagDisplayId':
      return item.tag_display_id;
    case 'bundle_no':
      return item.bundle_no;
    case 'cut_no':
      return item.cut_no ?? '';
    case 'user':
      return item.user;
    default:
      return item.bctx[header.key] ?? '';
  }
};

const getFooterConfig = (
  tableConfig: TBundleLineInTransactionLogPage['table_config'],
  totalQty: number,
  totalCount: number,
) => {
  const footerList = [
    ...tableConfig.table_main_headers,
    ...tableConfig.collapsible_list_config.table_in_row_column,
    'action-cell',
    'action-cell',
  ];

  const footerCols: { type: 'TEXT_CELL'; text: string | number }[] = footerList.map((col, index) => {
    if (index === 0) {
      return {
        type: 'TEXT_CELL',
        text: `TOTAL BUNDLES ${totalCount}`,
        align: 'left',
      };
    } else if (index === tableConfig.table_main_headers.length - 1) {
      return {
        type: 'TEXT_CELL',
        text: `TOTAL QTY ${totalQty}`,
        align: 'left',
      };
    } else {
      return {
        type: 'TEXT_CELL',
        text: '',
      };
    }
  });

  return footerCols;
};

const getHeaderConfig = (tableConfig: TBundleLineInTransactionLogPage['table_config'], isTabletScreen = false) => {
  const mainHeaders = tableConfig.table_main_headers.map((head, index) => {
    return {
      type: 'TEXT_CELL',
      text: head.display_name,
      align: 'left',
      ...(!isTabletScreen && {
        stickyType: 'LEFT',
        stickyPosition: index + 1,
      }),
      widthInPx: 120,
      maxWidthInPx: 120,
      ...(index === tableConfig.table_main_headers.length - 1 && { borderRight: true }),
    };
  });

  const secondaryHeaders = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    return {
      type: 'TEXT_CELL',
      text: head.display_name,
      align: 'left',
    };
  });

  const actionCells = [
    {
      type: 'TEXT_CELL',
      text: '',
      align: 'left',
      widthInPx: 40,
      stickyType: 'RIGHT',
    },
    {
      type: 'TEXT_CELL',
      text: '',
      align: 'left',
      widthInPx: 40,
      stickyType: 'RIGHT',
    },
  ];

  return [...mainHeaders, ...secondaryHeaders, ...actionCells];
};

const getRows = (
  item: TBundleLineInTransactionLogRow,
  tableConfig: TBundleLineInTransactionLogPage['table_config'],
) => {
  const mainRows = tableConfig.table_main_headers.map((head) => {
    const value = getValue(head, item);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  const secondaryRows = tableConfig.collapsible_list_config.table_in_row_column.map((head) => {
    const value = getValue(head, item);
    return {
      type: 'TEXT_CELL',
      text: value,
      align: 'left',
    };
  });

  const actionCells: (TActionCellType | TTextCell)[] = [
    {
      type: 'ACTION_CELL',
      actionType: 'EDIT',
      asIcon: true,
    },
    {
      type: 'ACTION_CELL',
      actionType: 'UNDO',
      asIcon: true,
    },
  ];

  return [...mainRows, ...secondaryRows, ...actionCells];
};

export const BUNDLE_LINE_IN_TRANSACTION_LOG_TABLE = {
  createModel: (
    items: TBundleLineInTransactionLogRow[],
    tableConfig: TBundleLineInTransactionLogPage['table_config'],
    totalCount: number,
    totalQty: number,
    isTabletScreen: boolean = false,
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: getHeaderConfig(tableConfig, isTabletScreen) as TTableOptions['headerConfig'],
        footerConfig: getFooterConfig(tableConfig, totalQty, totalCount),
      },
      (item) => {
        return {
          type: 'ROW',
          rowItem: item,
          cells: getRows(item, tableConfig) as TTableCellTree[],
        };
      },
    );
  },

  emptyModel: (
    items: TBundleLineInTransactionLogRow[],
    tableConfig: TBundleLineInTransactionLogPage['table_config'],
  ) => {
    return ModifiersZTableModel.createTableModel(
      items,
      {
        headerConfig: tableConfig.table_main_headers.map((head, index) => {
          return {
            type: 'TEXT_CELL' as const,
            text: head.display_name,
            align: 'left' as const,
          };
        }),
      },
      (item) => {
        return {
          type: 'ROW' as const,
          rowItem: item,
          cells: tableConfig.table_main_headers.map((head) => {
            const value = getValue(head, item);
            return {
              type: 'TEXT_CELL' as const,
              text: value,
              align: 'left' as const,
            };
          }),
        };
      },
    );
  },
};

export type TBundleLineInTransactionLogTableType = TTableTreeModel<TBundleLineInTransactionLogRow>;
